import React from 'react';
import { capitalize } from 'lodash';

import { paths } from 'config/path_helpers';
import { bugsnagNotify } from 'utilities/bugsnag';
import fetch from 'utilities/fetch';
import { formatPrice } from 'utilities/money';
import useAuthToken from '@magoosh/utils/use_authenticity_token';

import { Alert } from 'react-bootstrap';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';

import { AddOnOffer } from './types';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

interface ModalProps {
  addOnName: string;
  handleClose: (any) => void;
  successMessage?: string;
}

const Confirmation: React.FC<AddOnOffer & ModalProps> = (props) => {
  const { addOnName, handleClose, nextPath, orderItem, successMessage, cohortId } = props;
  const { couponCode, discountedPrice, fullPrice } = props.price;
  const { cardBrand, cardLast4, paymentMethod, stripePaymentMethodId } = props.paymentDetails;

  const SUCCESS = 'success';
  const ERROR = 'error';

  const authToken = useAuthToken();
  const [purchaseAttemptResult, setPurchaseAttemptResult] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);

  const stripe = useStripe();

  const handlePurchase = () => {
    setIsSubmitting(true);

    fetch(paths.api.subscriptions(), {
      method: 'POST',
      body: JSON.stringify({
        authenticityToken: authToken,
        couponCode: couponCode,
        orderItems: [orderItem],
        paymentMethod: paymentMethod,
        cohortId: cohortId,
        stripePaymentMethodId: stripePaymentMethodId
      })
    })
      .then((response) => {
        if (response._redirect) {
          setPurchaseAttemptResult(SUCCESS);
          setIsSubmitting(false);
        } else if (response.actionRequired) {
          stripe
            .confirmCardPayment(response.clientSecret, {
              payment_method: response.paymentMethodId,
              return_url: response.returnUrl,
              setup_future_usage: 'off_session'
            })
            .then((result) => {
              if (result.error) {
                setPurchaseAttemptResult(ERROR);
              } else {
                setPurchaseAttemptResult(SUCCESS);
              }
              setIsSubmitting(false);
            })
            .catch((error) => {
              setPurchaseAttemptResult(ERROR);
              bugsnagNotify(error);
              setIsSubmitting(false);
            });
        }
      })
      .catch((response) => {
        setPurchaseAttemptResult(ERROR);
        if (response.errors?._error) {
          setErrorMessage(response.errors._error);
        }
        bugsnagNotify(`Failed ${addOnName} add-on purchase`);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      {!purchaseAttemptResult && (
        <div className="u-margin-B-m">
          <p>
            Click "Purchase" below to confirm your purchase of {addOnName} for{' '}
            {formatPrice(discountedPrice, 'usd', true)}.
          </p>

          <p>
            <strong>Payment Method</strong>{' '}
            <small>
              (
              <a
                className="text-gray"
                href={`${paths.subscriptions(orderItem.purchasable.planSlug)}?coupon=${couponCode}`}
                target="_blank">
                change
              </a>
              )
            </small>
            :{' '}
            <span className="text-underdash">
              {capitalize(cardBrand)} ending in {cardLast4}
            </span>
          </p>
        </div>
      )}

      {!purchaseAttemptResult && (
        <div className="d-flex full-width">
          <div style={{ flex: 1 }}>
            <SecondaryButton block onClick={handlePurchase} submitting={isSubmitting}>
              Purchase
            </SecondaryButton>
          </div>

          <div className="u-margin-L-s" style={{ flex: 1 }}>
            <DefaultButton block onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </DefaultButton>
          </div>
        </div>
      )}

      {purchaseAttemptResult === SUCCESS && (
        <div>
          <Alert bsStyle="success">
            <p>Success! You have been charged {formatPrice(discountedPrice, 'usd', true)}.</p>

            {successMessage && <p>{successMessage}</p>}
          </Alert>

          <DefaultButton block href={nextPath || '/dashboard'}>
            {nextPath ? 'Continue' : 'Back to Dashboard'}
          </DefaultButton>
        </div>
      )}

      {purchaseAttemptResult === ERROR && (
        <div>
          <Alert bsStyle="danger">
            {errorMessage ? (
              errorMessage
            ) : (
              <>
                Oops, something went wrong with this purchase! Try{' '}
                <a
                  className="text-gray"
                  href={paths.subscriptions(orderItem.purchasable.planSlug)}
                  target="_blank">
                  changing your payment method
                </a>{' '}
                or continue on to your test prep dashboard.
              </>
            )}
          </Alert>

          <DefaultButton block href={nextPath}>
            Continue
          </DefaultButton>
        </div>
      )}
    </>
  );
};

const FormWrapper: React.FC<AddOnOffer & ModalProps> = (props) => {
  const stripePromise = loadStripe(props.stripeToken);
  return <Elements stripe={stripePromise}>{props.children}</Elements>;
};

export const WrappedConfirmation: React.FC<AddOnOffer & ModalProps> = (props) => {
  return (
    <FormWrapper {...props}>
      <Confirmation {...props} />
    </FormWrapper>
  );
};

export default WrappedConfirmation;
